import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react'

import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import ToggleInput from '~/components/Toggle'
import Tooltip from '~/components/Tooltip'
import { patch } from '~/modules/retailMedia/services/industry/ad'

import { useAdsTable } from '../_tables/AdsTable/hooks/useAdsTable'
import { useCampaignReadOnly } from '../../hooks/useCampaignReadOnly'

const ToggleAdverts = ({ data, showLabel = false }: ToggleAdvertsProps) => {
  const isReadOnlyView = useCampaignReadOnly()

  const [active, setActive] = useState<boolean>(data?.active)
  const { toast } = useContext(FeedbackContext)

  const { handleMutate } = useAdsTable()

  const label = useMemo(
    () => (active ? t('rm:ad.active') : t('rm:ad.deactivated')),
    [active]
  )

  /**
   * Fica visível e desabilitado, e não escondido: o toggle é também o indicador
   * de anúncio ativo ou pausado, então esconder tiraria a informação da tela.
   */
  const canEdit = useMemo(
    () => !!data?.canEditCampaign && !isReadOnlyView,
    [data, isReadOnlyView]
  )

  const isChecked = useMemo(() => data?.active, [data])

  const handleSuccess = useCallback(
    (value: boolean) => {
      handleMutate({ value, id: data.id })
    },
    [data?.id, handleMutate]
  )

  const handleChangeToggle = useCallback(
    async ({ value, onSuccess, data }: HandleChangeToggleProps) => {
      try {
        const id = data.id

        await patch({ id, body: { active: value } })

        if (value) {
          toast.success(t('rm:ad.active.description'))
        } else {
          toast.success(t('rm:ad.deactivated.description'))
        }

        onSuccess?.(value)
      } catch (error) {
        console.error(error)
        setActive(false)
        toast.critical(t('common:messages.errorOccurred.message'))
        onSuccess?.(!value)
      }
    },
    [toast]
  )

  type HandleChangeProps = {
    e: ChangeEvent<HTMLInputElement>
  }

  const handleChange = useCallback(
    ({ e }: HandleChangeProps) => {
      const value = e.target.checked

      setActive(value)

      handleChangeToggle({
        value,
        data,
        onSuccess: handleSuccess
      })
    },
    [data, handleChangeToggle, handleSuccess]
  )

  return (
    <Tooltip
      key={`toggle_adverts_${data?.id}`}
      interactiveTrigger
      icon={
        <ToggleInput
          key={`adverts_${data?.id}`}
          name={`adverts_${data?.id}`}
          isChecked={isChecked}
          label={showLabel ? label : null}
          onChange={handleChange}
          data-testid="toggleAdverts"
          disabled={!canEdit}
        />
      }
      text={label}
    />
  )
}

export default ToggleAdverts
